import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const PlayLevel1Fashion = ({ onWin, onLose }) => {
  const [timeLeft, setTimeLeft] = useState(30);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [seenIds] = useState(new Set());

  const imageSequence = [
    { 
      id: 1,
      targetId: "c7f1f69b-2874-4dd8-a2de-6f546f178548",
      url: "https://processed.portal.soot.com/playground-static-assets/fashion1.png",
    },
    { 
      id: 2,
      targetId: "56615004-f7fe-4cea-9510-ef360fedf812",
      url: "https://processed.portal.soot.com/playground-static-assets/fashion2.png"
    },
    { 
      id: 3,
      targetId: "9037162b-7377-464a-b26b-11592d7a8925",
      url: "https://processed.portal.soot.com/playground-static-assets/fashion3.png"
    }
  ];

  useEffect(() => {
    // Game timer
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          onLose();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    // Network request observer
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        const currentTargetId = imageSequence[currentImageIndex].targetId;
        
        // Skip if we've already seen this ID
        if (seenIds.has(currentTargetId)) return;
        
        // Check if the target ID appears in the request name
        if (entry.name.includes(currentTargetId)) {
          console.log('Found matching request:', entry.name);
          seenIds.add(currentTargetId);
          
          if (currentImageIndex < imageSequence.length - 1) {
            setCurrentImageIndex(prev => prev + 1);
            setTimeLeft(prev => prev + 30);
          } else {
            onWin(imageSequence);
          }
        }
      });
    });

    // Start observing resource timing entries
    observer.observe({ entryTypes: ['resource'] });
    
    return () => {
      clearInterval(timer);
      observer.disconnect();
    };
  }, [onWin, onLose, currentImageIndex, imageSequence, seenIds]);

  return (
    <div className="h-screen w-full relative">
      <iframe
        id="sootFrame"
        src="https://play.soot.com/virgilabloh"
        className="w-full h-full border-0"
        allow="same-origin"
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals"
      />

      {/* Target images panel */}
      <div className="fixed right-4 top-4 bottom-4 w-36 overflow-y-auto overflow-x-hidden space-y-4 p-2 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent">
        {imageSequence.map((image, index) => {
          if (index > currentImageIndex) return null;
          
          return (
            <motion.div 
              key={image.id}
              className="bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300"
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
            >
              <div className="aspect-square relative">
                <img
                  src={image.url}
                  alt={`Target ${index + 1}`}
                  className="w-full h-full object-cover transition-all duration-300"
                />
              </div>
              <div className="text-center py-2 bg-black text-white text-sm font-medium">
                {index === currentImageIndex ? 'FIND ME' : 'FOUND'}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Timer */}
      <div className="fixed top-4 left-4 px-4 py-2 bg-black bg-opacity-75 rounded-lg">
        <span className={`text-2xl font-bold ${timeLeft <= 10 ? 'text-red-500' : 'text-[#ECFF0F]'}`}>
          {timeLeft}s
        </span>
      </div>

      {/* Win simulation button */}
      <button
        onClick={() => {
          if (currentImageIndex < imageSequence.length - 1) {
            setCurrentImageIndex(prev => prev + 1);
            setTimeLeft(prev => prev + 30);
          } else {
            onWin(imageSequence);
          }
        }}
        className="fixed left-4 bottom-4 w-6 h-6 bg-black hover:bg-gray-700 rounded-full opacity-20 hover:opacity-40 transition-all duration-200"
        title="Simulate win"
      >
        <span className="sr-only">Win</span>
      </button>
    </div>
  );
};

export default PlayLevel1Fashion;